import {
  AttributeLookup,
  BundleLookup,
  FullAttribute,
  FullAttributeLookup,
  ScoreLookup,
  TrendviewLookup,
  AddonProductLookup,
  AttributeLike,
  DemographicsInformation,
  areAttributesSame,
} from '@ssar-app/domain';
import { GridItem, Heading, Grid, Text } from '@chakra-ui/react';

export interface CreditFieldsAndDeomgraphicsSummaryProps {
  scores: string[];
  bundles: string[];
  bundleLookups: BundleLookup[];
  fullAttributes: FullAttribute[];
  fullAttributeLookups: FullAttributeLookup[];
  scoreLookups: ScoreLookup[];
  trendviewFields: string[] | undefined;
  trendviewFieldLookups: TrendviewLookup[];
  addonProducts: string[];
  addonProductLookups: AddonProductLookup[];
  attributes: AttributeLike[];
  attributeLookups: AttributeLookup[];
  demographics?: DemographicsInformation;
}

const filterLookupsAndGetNames = <
  TLookup extends {
    code: string;
    name: string;
  },
>(
  lookupCodes: string[],
  lookups: TLookup[],
): string[] => {
  return (
    lookupCodes
      .map((s) => {
        const lookup = lookups.find(
          (a) => a?.code.toLowerCase() === s.toLowerCase(),
        );
        return lookup?.name || '';
      })
      .filter((a) => a.length > 0) || []
  );
};
export const CreditFieldsAndDeomgraphicsSummary = (
  props: CreditFieldsAndDeomgraphicsSummaryProps,
) => {
  const {
    scores,
    bundles,
    bundleLookups,
    fullAttributes,
    fullAttributeLookups,
    scoreLookups,
    trendviewFields,
    trendviewFieldLookups,
    addonProducts,
    addonProductLookups,
    attributes,
    attributeLookups,
    demographics,
  } = props;

  const scoresString =
    filterLookupsAndGetNames(scores, scoreLookups).join(', ') ||
    'No Scores were selected.';
  // console.log('ssss: ', scores, scoresString);

  const trendviewFieldsString = trendviewFields
    ? filterLookupsAndGetNames(trendviewFields, trendviewFieldLookups).join(
        ', ',
      ) || 'No Trendview fields were selected.'
    : undefined;

  const addonProductsString =
    filterLookupsAndGetNames(addonProducts, addonProductLookups).join(', ') ||
    'No Add-On Products were selected.';

  const attributeGroupsWithCount: Record<string, number> = {};
  attributes.forEach((attr) => {
    // TODO: (CV) Attibutes structure change
    const lookup = attributeLookups.find((a) => areAttributesSame(a, attr));
    const key = lookup ? lookup.group : attr.group;
    if (!attributeGroupsWithCount[key]) {
      attributeGroupsWithCount[key] = 0;
    }
    attributeGroupsWithCount[key] = attributeGroupsWithCount[key] + 1;
  });

  const bundleNames = bundles
    .map((b) => bundleLookups.find((a) => a.code === b)?.name || b)
    .filter((a) => !!a);

  const attributesString =
    bundleNames
      .concat(
        Object.keys(attributeGroupsWithCount).map(
          (key) => `${key} (${attributeGroupsWithCount[key]})`,
        ),
      )
      .join(', ') || 'No Attributes were selected.';

  const fullAttributeNames = fullAttributes
    .map(
      (fullAttribute) =>
        fullAttributeLookups.find((fa) => fa.code === fullAttribute?.code)
          ?.displayName || fullAttribute?.displayName,
    )
    .filter((fa) => !!fa);

  const fullAttributesString =
    fullAttributeNames.join(', ') ||
    'No Complete Attribute Sets were selected.';

  const demographicsArray: string[] = [];
  if (demographics?.shouldAppendDOBCCYYMMDD === true) {
    demographicsArray.push(`Date of Birth (CCYYMMDD)`);
  } else if (demographics?.shouldAppendDOBMMDDCCYY === true) {
    demographicsArray.push(`Date of Birth (MMDDCCYY)`);
  }
  if (demographics?.isEmploymentDataSelected) {
    demographicsArray.push('Employement Data');
  }
  if (demographics?.isPhoneNumberDataSelected) {
    demographicsArray.push('Phone Number Data');
  }
  if (demographics?.isExperianSocialSelected) {
    demographicsArray.push('Experian Social');
  }
  if (demographics?.isMlaInSelected) {
    demographicsArray.push('MLA_IN');
  }

  return (
    <>
      <Heading
        as={'h3'}
        textAlign={'center'}
        color={'#1d4f91'}
        marginTop={8}
        marginBottom={2}
        fontSize={'sm'}
        fontFamily={'Roboto'}
        fontWeight={'normal'}
      >
        Scores & Attributes
      </Heading>
      <Grid templateColumns='repeat(6, 1fr)' gap={2}>
        <GridItem textAlign={'right'}>
          <strong>Scores Selected:</strong>
        </GridItem>
        <GridItem colSpan={5} marginLeft={1}>
          {scoresString}
        </GridItem>
        {trendviewFieldsString && (
          <>
            <GridItem textAlign={'right'}>
              <strong>Trended Selected:</strong>
            </GridItem>
            <GridItem colSpan={5} marginLeft={1}>
              {trendviewFieldsString}
            </GridItem>
          </>
        )}
        {!trendviewFieldsString && (
          <>
            <GridItem textAlign={'right'}>
              <strong>Add-On Products Selected:</strong>
            </GridItem>
            <GridItem colSpan={5} marginLeft={1}>
              {addonProductsString}
            </GridItem>
          </>
        )}
        <GridItem textAlign={'right'}>
          <strong>Demographics Selected:</strong>
        </GridItem>
        <GridItem colSpan={5} marginLeft={1}>
          {demographicsArray.length > 0
            ? demographicsArray.join(', ')
            : 'No Demographics were selected.'}
        </GridItem>
        <GridItem textAlign={'right'}>
          <strong>Attributes Selected:</strong>
        </GridItem>
        <GridItem colSpan={5} marginLeft={1}>
          {attributesString ? attributesString : 'Loading Attributes Data..'}
        </GridItem>
        <GridItem textAlign={'right'}>
          <strong>Complete Attribute Sets Selected:</strong>
        </GridItem>
        <GridItem colSpan={5} marginLeft={1}>
          {fullAttributesString
            ? fullAttributesString
            : 'Loading Complete Attribute Sets Data..'}
        </GridItem>
      </Grid>
      <Text color='#1d4f91' textAlign='center'>
        Note: Please refer to the Output Layout for a complete list of
        attributes selected
      </Text>
    </>
  );
};
